import getClientConfig, { extractPresentationConfig } from '../config/clientConfig.helper.ts';
import getAppConfig from '../utils/appConfig.ts';
import { PayVaultAPIService } from '../api/payVault/payVaultAPI.tsx';
import { CountriesActions } from '../i18n/countries/countriesActions.ts';
import { appActionTypes, WHITELIST_ROUTES } from './app.constants';
import { loginActions } from '../login/login.actions';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import ErrorRedirectService from '../routes/error/errorRedirect.service';
import { TextHelpers } from '../utils/textHelpers';
import { SupportedLanguage } from '../config/constants/supportedLanguage.enum';
import { RegionHelper } from '../utils/RegionHelper';
import { initializeRAT } from '../utils/rat';
import { configureZxcvbn } from '../zxcvbn/index.js';
/**
 * Initialize application. including:
 * - app config
 * - client config
 * - member information, etc.
 */
function setAndSaveAppConfig() {
    return (dispatch, getState) => {
        getAppConfig().then(async (appConfig) => {
            const ENV = appConfig.ENV;
            if (ENV === 'local') {
                try {
                    const localSetupModule = await import('../localSetup.ts');

                    if (localSetupModule && localSetupModule.default) {
                        localSetupModule.default.setUpDevAPI(appConfig.REGION);
                        localSetupModule.default.setUpClient(appConfig.REGION);
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            initializeRAT(appConfig);
            PayVaultAPIService.init(appConfig.PAYVAULT_URL, appConfig.PAYVAULT_SERVICE_ID);
            dispatch(setAppConfig(appConfig, getState));
        });
    };

    function setAppConfig(appConfig, getState) {
        const activeClientId = webStorage.getActiveClientId();
        return (dispatch, getState) => {
            dispatch({
                type: appActionTypes.SET_APP_CONFIG,
                appConfig,
            });

            if (activeClientId) {
                dispatch(setAndSaveClientConfig(activeClientId));
            } else {
                // we don't need to redirect the user for whitelisted/static pages like terms and conditions page, cookie-policy page, etc.
                if (RegionHelper.isEU() && WHITELIST_ROUTES.includes(window.location.pathname)) {
                    // Default to SupportedLanguage.EN for whitelisted pages used in MyR EU like terms and conditions, cookie-policy, etc
                    const userSetLocale =
                        localStorage.getItem('userSetLocale') || SupportedLanguage.EN_UK;
                    dispatch(setAndSaveLocale(getState().app.locale, userSetLocale));
                } else {
                    const prefix = window.location.pathname.includes('/profile') ? '/profile' : '';
                    const lastVisitedPath = TextHelpers.setLastVisitedPathHandle(
                        window.location.pathname,
                        prefix,
                    );
                    ErrorRedirectService.noClientId(lastVisitedPath);
                }
            }
        };
    }
}

function setAndSaveClientConfig(activeClientId) {
    return (dispatch, getState) => {
        getClientConfig(activeClientId).then((clientConfig) => {
            const userSetLocale = localStorage.getItem('userSetLocale') || clientConfig.lang;
            dispatch(setAndSaveLocale(getState().app.locale, userSetLocale));
            setAccessibilityJs(clientConfig);
            document.documentElement.lang = userSetLocale ?? clientConfig.lang;
            dispatch(setClientConfig(clientConfig, getState));
            dispatch(CountriesActions.retrieveCountries(userSetLocale || clientConfig.lang));

            // Whether there is an access token or not, logged in or not, the page should load
            // anyway if it does not require login
            if (WHITELIST_ROUTES.includes(window.location.pathname)) {
                return;
            }

            if (webStorage.getAccessToken()) {
                dispatch(appActions.checkReadiness());
            } else {
                ErrorRedirectService.notLoggedIn();
            }
        });
    };

    function setClientConfig(clientConfig, getState) {
        const presentationConfig = extractPresentationConfig(clientConfig);
        return {
            type: appActionTypes.SET_CLIENT_CONFIG,
            clientConfig,
            presentationConfig,
        };
    }
}

function setAndSaveLocale(prevLocale, nextLocale) {
    return (dispatch, getState) => {
        localStorage.setItem('userSetLocale', nextLocale);
        configureZxcvbn(nextLocale);
        dispatch(setLocale(prevLocale, nextLocale, getState().app.clientConfig));
        dispatch(CountriesActions.retrieveCountries(nextLocale));
    };

    function setLocale(prevLocale, nextLocale, clientConfig) {
        return {
            type: appActionTypes.SET_LANGUAGE,
            nextLocale,
            clientConfig,
            meta: {
                rat: {
                    pData: {
                        ratContentLanguage: nextLocale,
                    },
                    cpData: {
                        from: prevLocale,
                        to: nextLocale,
                    },
                },
            },
        };
    }
}

function trackExternalLinkClick(name) {
    return (dispatch, getState) => {
        dispatch(goToRakService(name, getState().app.clientConfig));
    };

    function goToRakService(name, clientConfig) {
        return {
            type: appActionTypes.GO_TO_EXTERNAL_LINK,
            clientConfig,
            meta: {
                rat: {
                    cpData: {
                        Service: name,
                    },
                },
            },
        };
    }
}

function trackAgreement(data) {
    return (dispatch, getState) => {
        const { clientConfig, locale } = getState().app;

        dispatch(goToAgreement(data, clientConfig, locale));
    };

    function goToAgreement(data, clientConfig, locale) {
        const ratObject = {
            pData: {
                ratPageName: data.page,
            },
            cpData: {
                Agreement: data.destination,
            },
        };

        if (locale) {
            ratObject.pData.ratContentLanguage = locale;
        }

        return {
            type: appActionTypes.GO_TO_AGREEMENT,
            clientConfig,
            meta: {
                rat: ratObject,
            },
        };
    }
}

// check whether configuration is ready, if so, get user info.
function checkReadiness() {
    return (dispatch, getState) => {
        const isReady =
            Object.keys(getState().app.clientConfig).length > 0 &&
            Object.keys(getState().app.appConfig).length > 0;

        if (isReady) {
            if (Object.keys(getState().member).length === 0) {
                dispatch(loginActions.getMember());
            }

            dispatch({ type: appActionTypes.CONFIGS_LOADED });
        }
    };
}

function setAccessibilityJs(clientConfig) {
    if (clientConfig.accessibilityUrl) {
        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.src = clientConfig.accessibilityUrl;
        scriptElement.async = true;

        const headElement = document.querySelector('head');
        if (headElement) {
            headElement.appendChild(scriptElement);
        }
    }
}

export const appActions = {
    setAndSaveAppConfig,
    setAndSaveLocale,
    trackExternalLinkClick,
    trackAgreement,
    checkReadiness,
};
